import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Ico } from "../../../components/icons/icons.jsx";
import { useThemeSlice } from "../../../components/ui/StatusBadge.jsx";
import { FloatingPanel, useDismissOnOutsideClick } from "../../../components/ui/FloatingPanel.jsx";
import { ACCOUNT_STATUSES, STATUS_TINT } from "../accountConstants.js";

function accountInitials(name) {
  const parts = String(name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return String(name ?? "").replace(/[^a-zA-Z0-9]/g, "").slice(0, 2).toUpperCase() || "—";
}

function accountSubtitle(account) {
  return [[account.city, account.state].filter(Boolean).join(", "), account.tier].filter(Boolean).join(" · ");
}

function statusLabel(status) {
  return ACCOUNT_STATUSES.find((s) => s.value === status)?.label ?? status ?? "";
}

function accountSearchHaystack(account) {
  const contact = account.contact ?? {};
  return [account.companyName, account.city, account.state, account.tier, account.status, contact.name, contact.email]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

export function AccountSelectDropdown({ accounts, value, onChange }) {
  const { c, dark, accentHex: A } = useThemeSlice();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [hi, setHi] = useState(0);
  const anchorRef = useRef(null);
  const panelRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  const baseId = useId();

  const selected = useMemo(() => accounts.find((a) => a.id === value) ?? null, [accounts, value]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return accounts;
    return accounts.filter((a) => accountSearchHaystack(a).includes(q));
  }, [accounts, query]);

  const close = () => {
    setOpen(false);
    setQuery("");
  };

  useDismissOnOutsideClick(open, close, [anchorRef, panelRef]);

  useEffect(() => setHi(0), [open, query, matches.length]);
  useEffect(() => { if (open) searchRef.current?.focus(); }, [open]);
  useEffect(() => {
    listRef.current?.querySelector(`[data-idx="${hi}"]`)?.scrollIntoView({ block: "nearest" });
  }, [hi]);

  const select = (id) => {
    onChange(id);
    close();
  };

  const iconIdleBg = dark ? "#2A2D34" : "#ECE8E2";
  const iconIdleColor = dark ? "#8A8580" : "#7A7570";
  const panelShadow = dark ? "0 16px 44px rgba(0,0,0,.55)" : "0 16px 40px rgba(15,23,42,.14)";

  return (
    <div style={{ flex: "0 1 auto", minWidth: 0, maxWidth: "100%" }}>
      <button
        ref={anchorRef}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-controls={`${baseId}-list`}
        aria-haspopup="listbox"
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen(true);
          } else if (e.key === "Escape") close();
        }}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 10,
          maxWidth: "100%",
          padding: "2px 6px 2px 2px",
          borderRadius: 9,
          border: "none",
          background: open ? c.hover : "transparent",
          cursor: "pointer",
          textAlign: "left",
          outline: "none",
          transition: "background 0.15s ease",
        }}
      >
        <AccountAvatar
          initials={accountInitials(selected?.companyName)}
          selected
          accent={A}
          idleBg={iconIdleBg}
          idleColor={iconIdleColor}
          size={32}
        />
        <span style={{ minWidth: 0 }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap", maxWidth: "100%" }}>
            <span style={{ fontSize: 16, fontWeight: 800, color: c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", letterSpacing: "-0.01em", maxWidth: 280 }}>
              {selected?.companyName ?? "Select account"}
            </span>
            {selected?.status && <StatusChip status={selected.status} compact />}
          </span>
          {selected && (
            <span style={{ display: "block", marginTop: 2, fontSize: 11.5, color: c.sub, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 320 }}>
              {accountSubtitle(selected) || "—"}
            </span>
          )}
        </span>
        <span
          style={{
            display: "inline-flex",
            alignSelf: "center",
            color: c.mid,
            flexShrink: 0,
            opacity: open ? 1 : 0.65,
            transform: open ? "rotate(180deg)" : "none",
            transition: "transform 0.15s ease, opacity 0.15s ease",
          }}
        >
          <ChevronIcon />
        </span>
      </button>

      <FloatingPanel anchorRef={anchorRef} panelRef={panelRef} open={open} gap={8} minWidth={300} width={340}>
        <div style={{ background: c.card, border: `1px solid ${c.cardBdr}`, borderRadius: 12, boxShadow: panelShadow, overflow: "hidden" }}>
          <div style={{ height: 2, background: `linear-gradient(90deg, ${A} 0%, ${A}55 45%, ${A}00 100%)` }} />

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "10px 12px 8px" }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: c.sub }}>Switch account</span>
            <span style={{ fontSize: 10.5, fontWeight: 700, color: c.mid, background: c.hover, border: `1px solid ${c.cardBdr}`, borderRadius: 999, padding: "2px 8px" }}>
              {matches.length} {matches.length === 1 ? "client" : "clients"}
            </span>
          </div>

          <div style={{ padding: "0 10px 8px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 8, border: `1px solid ${c.cardBdr}`, background: c.hover }}>
              <span style={{ color: c.sub, display: "flex", opacity: 0.75, flexShrink: 0 }}>{Ico.search}</span>
              <input
                ref={searchRef}
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => handleSearchKeys(e, hi, matches, select, setHi, close)}
                placeholder="Search by name, city, or contact…"
                aria-label="Search accounts"
                style={{ width: "100%", border: "none", outline: "none", background: "transparent", fontSize: 12.5, color: c.text, fontFamily: "inherit" }}
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  aria-label="Clear search"
                  style={{ border: "none", background: "none", cursor: "pointer", color: c.sub, display: "flex", padding: 0, flexShrink: 0 }}
                >
                  <ClearIcon />
                </button>
              )}
            </div>
          </div>

          <ul
            id={`${baseId}-list`}
            ref={listRef}
            role="listbox"
            style={{ listStyle: "none", margin: 0, padding: "0 6px 8px", maxHeight: 300, overflow: "auto" }}
          >
            {matches.length === 0 ? (
              <li style={{ padding: "20px 12px", textAlign: "center" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: c.text, marginBottom: 4 }}>No accounts found</div>
                <div style={{ fontSize: 12, color: c.sub }}>Try a different search term</div>
              </li>
            ) : (
              matches.map((account, idx) => (
                <AccountOption
                  key={account.id}
                  account={account}
                  idx={idx}
                  active={account.id === value}
                  highlighted={idx === hi}
                  onHover={() => setHi(idx)}
                  onSelect={() => select(account.id)}
                  c={c}
                  dark={dark}
                  A={A}
                  iconIdleBg={iconIdleBg}
                  iconIdleColor={iconIdleColor}
                />
              ))
            )}
          </ul>
        </div>
      </FloatingPanel>
    </div>
  );
}

function AccountOption({ account, idx, active, highlighted, onHover, onSelect, c, dark, A, iconIdleBg, iconIdleColor }) {
  const sub = accountSubtitle(account);

  return (
    <li role="option" aria-selected={active} data-idx={idx} style={{ marginBottom: 2 }}>
      <button
        type="button"
        onMouseEnter={onHover}
        onClick={onSelect}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "8px 10px",
          border: "none",
          borderRadius: 8,
          background: active ? (dark ? `${A}18` : `${A}0c`) : highlighted ? c.hover : "transparent",
          cursor: "pointer",
          textAlign: "left",
          transition: "background 0.12s ease",
        }}
      >
        <AccountAvatar
          initials={accountInitials(account.companyName)}
          selected={active}
          accent={A}
          idleBg={iconIdleBg}
          idleColor={iconIdleColor}
          size={36}
        />
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0, marginBottom: sub ? 2 : 0 }}>
            <span style={{ fontSize: 13, fontWeight: active ? 700 : 600, color: active ? A : c.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {account.companyName}
            </span>
            {account.status && <StatusChip status={account.status} compact />}
          </span>
          {sub && (
            <span style={{ display: "block", fontSize: 11, color: c.sub, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {sub}
            </span>
          )}
        </span>
        {active && <CheckIcon color={A} />}
      </button>
    </li>
  );
}

function AccountAvatar({ initials, selected, accent, idleBg, idleColor, size }) {
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: size >= 36 ? 9 : 8,
        flexShrink: 0,
        background: selected ? accent : idleBg,
        color: selected ? "#fff" : idleColor,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size >= 36 ? 11 : 10,
        fontWeight: 700,
        letterSpacing: 0.3,
      }}
    >
      {initials}
    </span>
  );
}

function StatusChip({ status, compact }) {
  const tint = STATUS_TINT[status] ?? "#94A3B8";
  const label = statusLabel(status);
  if (!label) return null;

  return (
    <span
      style={{
        fontSize: compact ? 10 : 10.5,
        fontWeight: 700,
        padding: compact ? "1px 6px" : "2px 7px",
        borderRadius: 999,
        background: `${tint}18`,
        color: tint,
        textTransform: "capitalize",
        flexShrink: 0,
        lineHeight: 1.3,
      }}
    >
      {label}
    </span>
  );
}

function handleSearchKeys(e, hi, matches, select, setHi, close) {
  if (e.key === "ArrowDown") {
    e.preventDefault();
    setHi((i) => Math.min(i + 1, matches.length - 1));
  } else if (e.key === "ArrowUp") {
    e.preventDefault();
    setHi((i) => Math.max(i - 1, 0));
  } else if (e.key === "Enter") {
    e.preventDefault();
    if (matches[hi]) select(matches[hi].id);
  } else if (e.key === "Escape") {
    e.preventDefault();
    close();
  }
}

function ChevronIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function CheckIcon({ color }) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <polyline points="20,6 9,17 4,12" />
    </svg>
  );
}

function ClearIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
      <path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}
